"use client";

import { Play, Pause, ListVideo } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import VideoCardSkeleton from "../../skeletons/VideoCardSkeleton/VideoCardSkeleton";
import type { VerticalVideoCardProps } from "./VerticalVideoCard.types";
import PrivateCdnImage from "@/asset-delivery/components/PrivateCdnImage";

import "./VerticalVideoCard.css";

const VerticalVideoCard = ({
  title,
  videoSrc,
  thumbnail,
  thumbnailTarget,
  thumbnailKind = "thumbnail",
  thumbnailFieldName = "thumbnail",
  showPlaylistIcon = false,
  onPlaylistClick,
  className = "",
  onClick,
  iconSize = 28,
}: VerticalVideoCardProps) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isTitleExpanded, setIsTitleExpanded] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);

  // normalize nullable inputs for React attrs
  const src = videoSrc ?? undefined;
  const poster = thumbnail ?? undefined;

  const toggleTitle = () => setIsTitleExpanded((prev) => !prev);

  const handleTogglePlay = () => {
    const video = videoRef.current;
    if (!video) return;

    if (video.paused) {
      void video.play();
      setIsPlaying(true);
    } else {
      video.pause();
      setIsPlaying(false);
    }
  };

  // stop "playing" state when ended
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const handleEnded = () => setIsPlaying(false);
    video.addEventListener("ended", handleEnded);

    return () => video.removeEventListener("ended", handleEnded);
  }, []);

  return (
    <div
      className={`video-card-container ${className}`}
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onClick?.();
        }
      }}
    >
      <div className="video-thumbnail">
        {/* skeleton until poster/video loads */}
        {!isLoaded && (
          <>
            <VideoCardSkeleton variant="vertical" />

            {/* ✅ CDN thumbnail warmup */}
            {poster && thumbnailTarget && (
              <div style={{ display: "none" }} aria-hidden="true">
                <PrivateCdnImage
                  src={poster}
                  placeholderSrc={null}
                  alt="poster preloader"
                  target={thumbnailTarget}
                  kind={thumbnailKind as any}
                  fieldName={thumbnailFieldName as any}
                  rootMargin="900px"
                  threshold={0.01}
                  failOpen
                  imgLoading="eager"
                  fetchPriority="high"
                  onLoad={() => setIsLoaded(true)}
                  onError={() => {
                    // keep skeleton until video loads
                  }}
                />
              </div>
            )}

            {/* Optional fallback if target isn't provided */}
            {poster && !thumbnailTarget && (
              <img
                src={poster}
                alt="poster preloader"
                style={{ display: "none" }}
                onLoad={() => setIsLoaded(true)}
              />
            )}
          </>
        )}

        <video
          ref={videoRef}
          src={src}
          poster={poster}
          muted
          playsInline
          preload="metadata"
          className={`video-element ${isLoaded ? "loaded" : "loading"}`}
          onLoadedData={() => setIsLoaded(true)}
        />

        <button
          className="video-play-button"
          onClick={(e) => {
            e.stopPropagation(); // prevent navigation
            handleTogglePlay();
          }}
          aria-label={isPlaying ? "Pause video" : "Play video"}
        >
          {isPlaying ? (
            <Pause size={iconSize} className="video-play-icon" />
          ) : (
            <Play size={iconSize} className="video-play-icon" />
          )}
        </button>

        {title && (
          <div
            className={`video-title-overlay ${
              isTitleExpanded ? "expanded" : "collapsed"
            }`}
            onClick={(e) => {
              e.stopPropagation();
              toggleTitle();
            }}
            title={isTitleExpanded ? "Hide Title" : "Show Full Title"}
          >
            <p>{title}</p>
          </div>
        )}
      </div>

      {showPlaylistIcon && onPlaylistClick && (
        <button
          className="playlist-icon"
          onClick={(e) => {
            e.stopPropagation();
            onPlaylistClick();
          }}
          title="Go to Playlist"
        >
          <ListVideo size={iconSize} />
        </button>
      )}
    </div>
  );
};

export default VerticalVideoCard;
